import { createFileRoute } from '@tanstack/react-router';
import { StatusPill } from '@/components/ui/admin/dashboard';
import {
  Cell,
  DataGrid,
  GridHeader,
  GridRow,
  HeaderCell,
  RowAction,
  RowActions,
} from '@/components/ui/admin/data-grid';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { Icon } from '@/components/ui/icon';
import { useRouter } from '@/hooks/use-router';
import { date, localeFormat } from '@/lib/date';
import { meta } from '@/lib/meta';
import { TERM_KIND_LABELS, TERM_STATUS_LABELS } from '@/lib/terms/variables';
import { usePermissions } from '@/services/auth';
import {
  useCreateTermVersion,
  useNewDraftTermVersion,
  useTermVersions,
} from '@/services/term-versions';

export const Route = createFileRoute('/_app/term-versions/')({
  head: () => meta({ label }),
  component: RouteComponent,
});

const label = 'Termos';
const model = 'term-versions';

type Kind = keyof typeof TERM_KIND_LABELS;
type Status = keyof typeof TERM_STATUS_LABELS;

const KIND_DESCRIPTIONS: Record<Kind, string> = {
  participation: 'Aceito por todo recruta na inscrição do site.',
  minor_authorization: 'Aceito pelo responsável quando o recruta é menor de 18 anos.',
};

const STATUS_TONE: Record<Status, 'olive' | 'sand' | 'muted'> = {
  published: 'olive',
  draft: 'sand',
  archived: 'muted',
};

const COLUMNS = '84px minmax(200px,1fr) 150px 170px 180px 88px';

function RouteComponent() {
  const { push } = useRouter();
  const { createPermissions } = usePermissions();
  const { canUpdate } = createPermissions(model);

  const { data, isLoading } = useTermVersions({
    perPage: 100,
    orderBy: 'version',
    order: 'desc',
  } as any);
  const { mutateAsync: createDraft, isPending: isCreating } = useCreateTermVersion();
  const { mutateAsync: newDraft, isPending: isDrafting } = useNewDraftTermVersion();

  const versions = ((data as any)?.data ?? []) as any[];
  const open = (id: string) => push(`/${model}/${id}`);

  const handleNewVersion = async (kind: Kind) => {
    const current = versions.find((v) => v.kind === kind && v.status === 'published');
    const created: any = current
      ? await newDraft(current.id)
      : await createDraft({ kind, title: TERM_KIND_LABELS[kind] } as any);
    if (created?.id) open(created.id);
  };

  return (
    <Content
      isLoading={isLoading || isCreating || isDrafting}
      breadcrumbs={{
        icon: 'scroll-text',
        title: label,
        items: [{ label: 'Configurações' }, { label, active: true }],
      }}
    >
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5">
        {(Object.keys(TERM_KIND_LABELS) as Kind[]).map((kind) => {
          const ofKind = versions.filter((v) => v.kind === kind);
          const current = ofKind.find((v) => v.status === 'published');
          const draft = ofKind.find((v) => v.status === 'draft');

          return (
            <section
              key={kind}
              className="overflow-hidden rounded-[16px] border border-border bg-card"
            >
              <header className="flex flex-wrap items-center gap-3 border-b border-border px-[26px] py-5">
                <span className="mr-auto flex min-w-0 flex-col gap-1.5">
                  <span className="font-display text-[18px] font-extrabold uppercase tracking-tight text-white">
                    {TERM_KIND_LABELS[kind]}
                  </span>
                  <span className="text-[13px] text-muted-foreground">
                    {KIND_DESCRIPTIONS[kind]}{' '}
                    {current ? (
                      <span className="text-olive-ink">
                        Vigente: versão {current.version}, publicada em{' '}
                        {date(current.publishedAt).format(localeFormat)}.
                      </span>
                    ) : (
                      <span className="text-sand">Nenhuma versão publicada ainda.</span>
                    )}
                  </span>
                </span>

                {canUpdate &&
                  (draft ? (
                    <Button
                      variant="outline"
                      size="md"
                      icon="pencil"
                      onClick={() => open(draft.id)}
                    >
                      Continuar rascunho (v{draft.version})
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="md"
                      icon="file-plus-2"
                      onClick={() => handleNewVersion(kind)}
                    >
                      {current ? 'Nova versão' : 'Criar primeira versão'}
                    </Button>
                  ))}
              </header>

              {ofKind.length === 0 ? (
                <p className="m-0 px-[26px] py-8 text-center text-[13.5px] text-muted-foreground">
                  Nenhuma versão cadastrada.
                </p>
              ) : (
                <DataGrid
                  columns={COLUMNS}
                  minWidth={760}
                  className="rounded-none border-0"
                >
                  <GridHeader>
                    <HeaderCell>Versão</HeaderCell>
                    <HeaderCell>Título</HeaderCell>
                    <HeaderCell>Situação</HeaderCell>
                    <HeaderCell>Publicada em</HeaderCell>
                    <HeaderCell>Criada por</HeaderCell>
                    <HeaderCell align="right">Ações</HeaderCell>
                  </GridHeader>
                  {ofKind.map((v) => (
                    <GridRow key={v.id} onClick={() => open(v.id)}>
                      <Cell tone="strong">
                        <span className="font-display text-base font-extrabold">
                          v{v.version}
                        </span>
                      </Cell>
                      <Cell tone="strong" truncate>
                        {v.title}
                      </Cell>
                      <Cell>
                        <StatusPill tone={STATUS_TONE[v.status as Status]}>
                          {TERM_STATUS_LABELS[v.status as Status]}
                        </StatusPill>
                      </Cell>
                      <Cell tone="meta">
                        {v.publishedAt ? date(v.publishedAt).format(localeFormat) : '—'}
                      </Cell>
                      <Cell tone="meta" truncate>
                        {v.createdBy?.name ?? '—'}
                      </Cell>
                      <RowActions>
                        <RowAction
                          tone={v.status === 'draft' ? 'accent' : 'neutral'}
                          title={v.status === 'draft' ? 'Editar' : 'Visualizar'}
                          onClick={() => open(v.id)}
                        >
                          <Icon
                            name={v.status === 'draft' ? 'pencil' : 'eye'}
                            className="size-4"
                          />
                        </RowAction>
                      </RowActions>
                    </GridRow>
                  ))}
                </DataGrid>
              )}
            </section>
          );
        })}
      </div>
    </Content>
  );
}
